/**
 * Permission Constants
 *
 * Granular permissions for Invento's RBAC system.
 * Format: resource:action (e.g., product:create)
 */
export const BUSINESS_PERMISSIONS = {
    READ: 'business:read',
    UPDATE: 'business:update',
};
export const SHOP_PERMISSIONS = {
    CREATE: 'shop:create',
    READ: 'shop:read',
    UPDATE: 'shop:update',
    DELETE: 'shop:delete',
};
export const PRODUCT_PERMISSIONS = {
    CREATE: 'product:create',
    READ: 'product:read',
    UPDATE: 'product:update',
    DELETE: 'product:delete',
    NOTE: 'product:note',
    PRICE_READ: 'product:price:read',
    PRICE_UPDATE: 'product:price:update',
};
export const INVENTORY_PERMISSIONS = {
    READ: 'inventory:read',
    RECEIVE: 'inventory:receive',
    ADJUST: 'inventory:adjust',
    TRANSFER_CREATE: 'inventory:transfer:create',
    TRANSFER_RECEIVE: 'inventory:transfer:receive',
    TRANSFER_CANCEL: 'inventory:transfer:cancel',
    STOCK_CROSS_BRANCH_VIEW: 'inventory:stock:cross_branch_view',
    STOCK_CROSS_BRANCH_TRANSFER: 'inventory:stock:cross_branch_transfer',
    CROSS_BRANCH_WRITE: 'inventory:cross_branch_write',
};
export const ORDER_PERMISSIONS = {
    CREATE: 'order:create',
    READ: 'order:read',
    UPDATE: 'order:update',
    CANCEL: 'order:cancel',
};
export const SALES_PERMISSIONS = {
    READ: 'sales:read',
    INVOICE_CREATE: 'sales:invoice:create',
    INVOICE_UPDATE: 'sales:invoice:update',
    INVOICE_VOID: 'sales:invoice:void',
    QUOTE_CREATE: 'sales:quote:create',
    QUOTE_UPDATE: 'sales:quote:update',
    QUOTE_CONVERT: 'sales:quote:convert',
    ORDER_CREATE: 'sales:order:create',
    ORDER_UPDATE: 'sales:order:update',
    ORDER_INVOICE: 'sales:order:invoice',
    RETURN_READ: 'sales:return:read',
    RETURN_CREATE: 'sales:return:create',
    RETURN_PROCESS: 'sales:return:process',
    RETURN_VOID: 'sales:return:void',
    CREDIT_NOTE_READ: 'sales:credit_note:read',
    CREDIT_NOTE_CREATE: 'sales:credit_note:create',
    CREDIT_NOTE_APPLY: 'sales:credit_note:apply',
    CREDIT_NOTE_REFUND: 'sales:credit_note:refund',
    CREDIT_NOTE_VOID: 'sales:credit_note:void',
    PAYMENT_CREATE: 'sales:payment:create',
    STATEMENT_CREATE: 'sales:statement:create',
    STATEMENT_SEND: 'sales:statement:send',
    POS_CREATE: 'sales:pos:create',
    POS_VOID: 'sales:pos:void',
    POS_SHIFT_CLOSE: 'sales:pos:shift:close',
    POS_SHIFT_VARIANCE_APPROVE: 'sales:pos:shift:variance:approve',
    FISCALIZATION_RETRY: 'sales:fiscalization:retry',
};
export const CUSTOMER_PERMISSIONS = {
    CREATE: 'customer:create',
    READ: 'customer:read',
    UPDATE: 'customer:update',
    DELETE: 'customer:delete',
    ARCHIVE: 'customer:archive',
    STATEMENT_READ: 'customer:statement:read',
    CROSS_BRANCH_READ: 'customer:cross_branch_read',
};
export const SUPPLIER_PERMISSIONS = {
    CREATE: 'supplier:create',
    READ: 'supplier:read',
    UPDATE: 'supplier:update',
    ARCHIVE: 'supplier:archive',
};
export const PURCHASES_PERMISSIONS = {
    READ: 'purchases:read',
    PO_CREATE: 'purchases:po:create',
    PO_UPDATE: 'purchases:po:update',
    PO_CANCEL: 'purchases:po:cancel',
    BILL_CREATE: 'purchases:bill:create',
    BILL_VOID: 'purchases:bill:void',
    PAYMENT_CREATE: 'purchases:payment:create',
    DOCUMENT_NOTE: 'purchases:document:note',
    PAYMENT_NOTE: 'purchases:payment:note',
    PAYMENT_VOID: 'purchases:payment:void',
    RETURN_READ: 'purchases:return:read',
    RETURN_CREATE: 'purchases:return:create',
    RETURN_COMPLETE: 'purchases:return:complete',
    SUPPLIER_CREDIT_READ: 'purchases:supplier_credit:read',
    SUPPLIER_CREDIT_APPLY: 'purchases:supplier_credit:apply',
    CROSS_BRANCH_READ: 'purchases:cross_branch_read',
    CROSS_BRANCH_WRITE: 'purchases:cross_branch_write',
};
export const MONEY_PERMISSIONS = {
    ACCOUNT_READ: 'money:account:read',
    ACCOUNT_CREATE: 'money:account:create',
    ACCOUNT_UPDATE: 'money:account:update',
    TRANSACTION_READ: 'money:transaction:read',
    TRANSACTION_POST: 'money:transaction:post',
    TRANSACTION_VOID: 'money:transaction:void',
    TRANSACTION_RECATEGORISE: 'money:transaction:recategorise',
    RECONCILIATION_READ: 'money:reconciliation:read',
    RECONCILIATION_MATCH: 'money:reconciliation:match',
    RECONCILIATION_CLOSE: 'money:reconciliation:close',
    RECONCILIATION_IMPORT: 'money:reconciliation:import',
    RECONCILIATION_CORRECT: 'money:reconciliation:correct',
    CUSTOMER_CREDIT_READ: 'money:customer_credit:read',
    CUSTOMER_CREDIT_APPLY: 'money:customer_credit:apply',
    CUSTOMER_CREDIT_REFUND: 'money:customer_credit:refund',
    CROSS_BRANCH_READ: 'money:cross_branch_read',
    CROSS_BRANCH_WRITE: 'money:cross_branch_write',
};
export const CATEGORY_PERMISSIONS = {
    CREATE: 'category:create',
    READ: 'category:read',
    UPDATE: 'category:update',
    DELETE: 'category:delete',
};
export const BRAND_PERMISSIONS = {
    CREATE: 'brand:create',
    READ: 'brand:read',
    UPDATE: 'brand:update',
    DELETE: 'brand:delete',
};
export const USER_PERMISSIONS = {
    CREATE: 'user:create',
    READ: 'user:read',
    UPDATE: 'user:update',
    DELETE: 'user:delete',
    INVITE: 'user:invite',
};
export const REPORT_PERMISSIONS = {
    READ: 'report:read',
};
export const SETTINGS_PERMISSIONS = {
    READ: 'settings:read',
    UPDATE: 'settings:update',
};
export const SUPPORT_PERMISSIONS = {
    READ_ALL: 'support:read_all',
    REPLY: 'support:reply',
    UPDATE: 'support:update',
};
/**
 * Flat array of all permission values (for seeding, iteration)
 */
export const ALL_PERMISSION_VALUES = [
    ...Object.values(BUSINESS_PERMISSIONS),
    ...Object.values(SHOP_PERMISSIONS),
    ...Object.values(PRODUCT_PERMISSIONS),
    ...Object.values(INVENTORY_PERMISSIONS),
    ...Object.values(ORDER_PERMISSIONS),
    ...Object.values(SALES_PERMISSIONS),
    ...Object.values(CUSTOMER_PERMISSIONS),
    ...Object.values(SUPPLIER_PERMISSIONS),
    ...Object.values(PURCHASES_PERMISSIONS),
    ...Object.values(MONEY_PERMISSIONS),
    ...Object.values(CATEGORY_PERMISSIONS),
    ...Object.values(BRAND_PERMISSIONS),
    ...Object.values(USER_PERMISSIONS),
    ...Object.values(REPORT_PERMISSIONS),
    ...Object.values(SETTINGS_PERMISSIONS),
    ...Object.values(SUPPORT_PERMISSIONS),
];
//# sourceMappingURL=permissions.js.map